// Opens and focuses tabs in the central dock area, and knows which one is in front.
import type { DockviewApi } from 'dockview-react';
import { create } from 'zustand';
import { findSet, refKey, useWorkspace, type ModelRef } from './workspace';

let api: DockviewApi | null = null;

export interface ActiveTab {
  /** The tab in front, by panel id (`diagram:…`, `timeline:…`, `note:…`, `files`), or null without tabs. */
  id: string | null;
  /** What the inspector and the explorer's entity list are about: the tab in front, or while a source file is in front, the tab before it. */
  subject: string | null;
}

export const useActiveTab = create<ActiveTab>(() => ({ id: null, subject: null }));

/** A panel id's kind: `diagram`, `timeline`, `sketch`, `compare`, `note`, `source`, `codemap`, `files`… */
export function tabKind(id: string | null): string | null {
  return id === null ? null : id.split(':', 1)[0]!;
}

/** Tabs that show a model: the inspector shows its selection and the explorer its entities. */
export const MODEL_TABS = new Set(['diagram', 'timeline', 'sketch', 'compare']);

/** Tabs with nothing for the inspector to show: it steps aside while they are in front. */
export const NO_INSPECTOR = new Set(['files', 'graph']);

export function setActiveTab(id: string | null): void {
  useActiveTab.setState((s) => ({ id, subject: tabKind(id) === 'source' ? s.subject : id }));
}

export function setDockApi(value: DockviewApi | null): void {
  api = value;
}

export function modelTitle(ref: ModelRef): string {
  if (ref.sketchId) return useWorkspace.getState().sketches[ref.sketchId]?.doc.title ?? 'Sketch';
  const set = findSet(ref.setKey);
  const context = set?.contextName ?? 'Model';
  if (!ref.migrationId) return context;
  const migration = set?.migrations.find((m) => m.id === ref.migrationId);
  return `${context} · ${migration?.name ?? ref.migrationId}`;
}

export function openDiagram(ref: ModelRef): void {
  if (!api) return;
  const id = `diagram:${refKey(ref)}`;
  const existing = api.getPanel(id);
  if (existing) {
    existing.api.setActive();
    return;
  }
  api.addPanel({ id, component: 'diagram', title: modelTitle(ref), params: { modelRef: ref } });
}

/** Opens the timeline of a context (one tab per context) at a migration, or at the latest one. */
export function openTimeline(setKey: string, migrationId: string | null = null): void {
  if (!api) return;
  const id = `timeline:${setKey}`;
  const existing = api.getPanel(id);
  if (existing) {
    if (migrationId !== null) existing.api.updateParameters({ setKey, migrationId, nonce: Date.now() });
    existing.api.setActive();
    return;
  }
  const context = findSet(setKey)?.contextName ?? 'Model';
  api.addPanel({ id, component: 'timeline', title: `${context} history`, params: { setKey, migrationId, nonce: Date.now() } });
}

export function openSketch(sketchId: string): void {
  if (!api) return;
  const id = `sketch:${sketchId}`;
  const existing = api.getPanel(id);
  if (existing) {
    existing.api.setActive();
    return;
  }
  api.addPanel({ id, component: 'sketch', title: modelTitle({ setKey: '', migrationId: null, sketchId }), params: { sketchId } });
}

/** Opens a comparison of two or more models (one tab per set of models). */
export function openCompare(refs: ModelRef[]): void {
  if (!api || refs.length < 2) return;
  const id = `compare:${refs.map(refKey).join('+')}`;
  const existing = api.getPanel(id);
  if (existing) {
    existing.api.setActive();
    return;
  }
  api.addPanel({ id, component: 'compare', title: `Compare ${refs.map(modelTitle).join(' · ')}`, params: { refs } });
}

export function openNote(noteId: string): void {
  if (!api) return;
  const id = `note:${noteId}`;
  const existing = api.getPanel(id);
  if (existing) {
    existing.api.setActive();
    return;
  }
  api.addPanel({ id, component: 'note', title: useWorkspace.getState().notes[noteId]?.title ?? 'Note', params: { noteId } });
}

/** Opens the code map (one tab per folder). */
export function openCodeMap(): void {
  if (!api) return;
  const existing = api.getPanel('codemap');
  if (existing) {
    existing.api.setActive();
    return;
  }
  api.addPanel({ id: 'codemap', component: 'codemap', title: 'Code map', params: {} });
}

/** Opens the endpoint map (one tab per folder). */
export function openEndpointMap(): void {
  if (!api) return;
  const existing = api.getPanel('endpoints');
  if (existing) {
    existing.api.setActive();
    return;
  }
  api.addPanel({ id: 'endpoints', component: 'endpoints', title: 'Endpoints', params: {} });
}

/** Opens the test map (one tab per folder). */
export function openTestMap(): void {
  if (!api) return;
  const existing = api.getPanel('tests');
  if (existing) {
    existing.api.setActive();
    return;
  }
  api.addPanel({ id: 'tests', component: 'tests', title: 'Tests', params: {} });
}

/** Opens the Files tab: every file, and what each commit changed (one tab per folder). */
export function openFiles(): void {
  if (!api) return;
  const existing = api.getPanel('files');
  if (existing) {
    existing.api.setActive();
    return;
  }
  api.addPanel({ id: 'files', component: 'files', title: 'Files', params: {} });
}

export function openGraph(): void {
  if (!api) return;
  const existing = api.getPanel('graph');
  if (existing) {
    existing.api.setActive();
    return;
  }
  api.addPanel({ id: 'graph', component: 'graph', title: 'Graph', params: {} });
}

export function closePanel(id: string): void {
  api?.getPanel(id)?.api.close();
}

export function openSourceFile(path: string, line?: number): void {
  if (!api) return;
  const id = `source:${path}`;
  const existing = api.getPanel(id);
  const title = path.slice(path.lastIndexOf('/') + 1);
  if (existing) {
    existing.api.updateParameters({ path, line, nonce: Date.now() });
    existing.api.setActive();
    return;
  }
  api.addPanel({ id, component: 'source', title, params: { path, line, nonce: Date.now() } });
}

export function closeAllPanels(): void {
  if (!api) return;
  for (const panel of [...api.panels]) panel.api.close();
}
